import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdDelete, MdExtension, MdLayers } from "react-icons/md";
import { createApi } from "../../../../core/api";

const ModuleTypeText: Record<number, string> = {
  1: "Core",
  2: "Extra",
};

const ModuleTypeStyle: Record<number, { color: string; bg: string }> = {
  1: { color: "#2563EB", bg: "rgba(37,99,235,.15)" },
  2: { color: "#ee0ad0ff", bg: "rgba(249,115,22,.18)" },
};

const DEFAULT_MODULE_TYPE = 1;

export const ModuleCrud = () => {
  const api = useMemo(() => createApi(), []);
  const navigate = useNavigate();
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [moduleType, setModuleType] = useState<number>(1);

  const [coreModules, setCoreModules] = useState<any[]>([]);
  const [selectedCoreId, setSelectedCoreId] = useState<number | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.module
      .getAll()
      .then((r) => {
        if (r?.ok) {
          const list = r.data?.data ?? r.data ?? [];
          setData(Array.isArray(list) ? list : []);
        }
      })
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Modules",
        items: [{ label: "Modules", route: "/dashboard/module" }],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setCode("");
    setModuleType(1);
    setSelectedCoreId(null);
    setError(null);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setName(row?.name ?? "");
    setCode(row?.code ?? "");
    setModuleType(row?.moduleType ?? 1);
    setSelectedCoreId(null);
    setError(null);
    setOpen(true);
  };

  useEffect(() => {
    if (moduleType === 2) {
      setCoreModules(
        data.filter((x: any) => x.moduleType === 1 || x.moduleType === 0)
      );
    } else {
      setCoreModules([]);
      setSelectedCoreId(null);
    }
  }, [moduleType]);

  useEffect(() => {
    if (moduleType === 2 && selectedCoreId && coreModules.length) {
      const core = coreModules.find((x) => x.id === selectedCoreId);
      if (core) setCode(core.code);
    }
  }, [moduleType, selectedCoreId, coreModules]);

  const submit = async () => {
    if (!name.trim() || saving) return;
    if (moduleType === 1 && !code.trim()) return;
    if (moduleType === 2 && !selectedCoreId) return;

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name,
        code,
        moduleType,
      };

      if (editing) {
        const r = await api.module.update(editing.id, payload);
        if (!r?.ok) throw new Error(r?.message || "Update failed");
      } else {
        const r = await api.module.create(payload);
        if (!r?.ok) throw new Error(r?.message || "Create failed");
      }

      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.module.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");

      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "name",
        title: "Name",
        minWidth: 240,
        accessor: (r: any) => r.name,
        render: (_: any, v: string) => (
          <Badge
            icon={MdExtension}
            label={v}
            color="#7C3AED"
            bg="rgba(124,58,237,.15)"
          />
        ),
      },
      {
        key: "code",
        title: "Code",
        minWidth: 220,
        accessor: (r: any) => r.code,
      },
      {
        key: "type",
        title: "Type",
        minWidth: 140,
        render: (r: any) => {
          const type = r?.moduleType ?? DEFAULT_MODULE_TYPE;
          const style =
            ModuleTypeStyle[type] ?? ModuleTypeStyle[DEFAULT_MODULE_TYPE];

          return (
            <Badge
              label={
                ModuleTypeText[type] ?? ModuleTypeText[DEFAULT_MODULE_TYPE]
              }
              color={style.color}
              bg={style.bg}
            />
          );
        },
      },
      {
        key: "packageCount",
        title: "Packages",
        minWidth: 140,
        accessor: (r: any) => r.packages?.length ?? 0,
        render: (_: any, v: number) => (
          <Badge
            label={`${v} Package`}
            color="#059669"
            bg="rgba(5,150,105,.15)"
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 240,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "manage",
                icon: MdLayers,
                color: "#059669",
                onClick: () => navigate(`/dashboard/module/${row.id}/packages`),
              },
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Modules</div>
          <div style={S.subTitle}>Manage system modules</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Create Module
        </Button>
      </div>

      <Table
        tableKey="module"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Module" : "Create Module"}
        </div>

        <div style={S.form}>
          <label style={S.label}>Module Type</label>
          <select
            value={moduleType}
            onChange={(e) => setModuleType(Number(e.target.value))}
            style={S.input}
            disabled={saving}
          >
            <option value={1}>Core</option>
            <option value={2}>Extra</option>
          </select>

          {moduleType === 2 && (
            <>
              <label style={S.label}>Core Module</label>
              <select
                value={selectedCoreId ?? ""}
                onChange={(e) => setSelectedCoreId(Number(e.target.value))}
                style={S.input}
                disabled={saving}
              >
                <option value="">Select Core Module</option>
                {coreModules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <label style={S.label}>Module Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={S.input}
            disabled={saving}
          />

          <label style={S.label}>Code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={S.input}
            disabled={saving || moduleType === 2}
          />

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Module</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this module?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            loading={deleting}
            disabled={deleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: { fontSize: 13, color: "#64748B" },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
